import styles from "./OnboardingBanner.module.scss";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import { clsx } from "clsx";
import { ReactNode } from "react";
import { useIntl } from "react-intl";
import MiscellaneousXMedium from "../../../../common/icons/MiscellaneousXMedium.tsx";
import useUserPreference from "../../actions/user/useUserPreference.ts";
import { UserPreferenceKey } from "../../../../common/api/decimal/methods/user.ts";
import { focusMainContent } from "../../../../common/utils/focus.ts";

interface OnboardingBannerProps {
  title: string;
  body: ReactNode;
  linkText?: string;
  href?: string;
  onClick?: () => void;
  to?: string;
  icon?: ReactNode;
  leftContent?: ReactNode;
  noMargin?: boolean;
  bannerSeenPreferenceKey?: UserPreferenceKey;
  badgeText?: ReactNode;
  dismissOnClick?: boolean;
}

export default function OnboardingBanner({
  title,
  body,
  linkText,
  href,
  onClick,
  to,
  icon,
  leftContent,
  noMargin,
  bannerSeenPreferenceKey,
  badgeText,
  dismissOnClick,
}: OnboardingBannerProps) {
  const intl = useIntl();
  const [hasBeenSeen, setHasBeenSeen] = useUserPreference<boolean>(
    bannerSeenPreferenceKey
  );

  if (hasBeenSeen) {
    return null;
  }

  const leftSideGraphic =
    leftContent ?? (icon ? <div className={styles.icon}>{icon}</div> : null);

  function dismiss() {
    setHasBeenSeen(true);
    focusMainContent();
  }

  const finalOnClick = dismissOnClick ? dismiss : onClick;

  return (
    <div className={clsx(styles.container, noMargin && styles.noMargin)}>
      <div
        className={clsx(styles.banner)}
        role="region"
        aria-label={intl.formatMessage({ defaultMessage: "Banner" })}
      >
        {leftSideGraphic}
        <div className={styles.textAndButton}>
          <div className={styles.textContent}>
            {badgeText && (
              <div
                className={clsx(
                  "theme-accent",
                  "vst-tag",
                  "vst-tag-variant-dark",
                  "vst-tag-size-tiny",
                  "vst-tag-option-strong",
                  styles.badge
                )}
              >
                {badgeText}
              </div>
            )}
            <h2 className={styles.header}>{title}</h2>
            <div className={clsx("vst-pretty-html", styles.body)}>{body}</div>
          </div>
          {(href ?? finalOnClick ?? to) && (
            <BaseLinkOrButton
              className={clsx(
                "vst-button-pill",
                "vst-button-variant-outline",
                styles.mainLink
              )}
              href={href}
              onClick={finalOnClick}
              to={to}
            >
              {linkText}
            </BaseLinkOrButton>
          )}
        </div>
        {bannerSeenPreferenceKey && (
          <BaseLinkOrButton
            className={clsx(
              "vst-icon-button-circle",
              "vst-icon-button-variant-black",
              styles.closeButton
            )}
            aria-label={intl.formatMessage({ defaultMessage: "Close" })}
            data-tooltip-content={intl.formatMessage({
              defaultMessage: "Close",
            })}
            onClick={dismiss}
          >
            <MiscellaneousXMedium />
          </BaseLinkOrButton>
        )}
      </div>
    </div>
  );
}
